<script setup>
import { computed, getCurrentInstance, provide, reactive, ref } from "vue";
import { useForm } from "@/form";
import MessageBox from "./Partials/MessageBox.vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import LanguageMenu from "@Components/LanguageMenu.vue";
import Models from "./Partials/Models.vue";
import Messages from "./Partials/Messages/Messages.vue";
import Processing from "./Partials/Processing.vue";
import Preview from "./Partials/Preview/Preview.vue";
import { PrimaryButton } from "@Components/Button";
import Progress from "./Partials/Progress.vue";
import ProgressLogs from "./Partials/ProgressLogs.vue";
import Suggestions from "./Partials/Suggestions.vue";
import { reorderCategories } from "./utils";
import _findIndex from "lodash/findIndex";

const { t: __ } = useI18n();
const router = useRouter();

const [profiles, categories] = await Promise.all([
    new Promise((resolve, reject) => {
        useForm()
            .post("personas_index")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_categories")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

const chat = useForm({
    messages: [],
    language: getCurrentInstance().appContext.config.globalProperties.$app.default_locale,
});

const displayableMessages = ref([]);
const message = ref("");
const started = ref(false);
const isTerminated = ref(false);

const suggestions = [
    {
        description: __("Crée un projet de contenu complet sur le thème des voyages en Polynésie."),
    },
    {
        description: __("Génère une image d'un hamburger dans une assiette."),
    },
    {
        description: __("Crée une structure de page en silo sur le thème des constructions de maison en bois."),
    },
    {
        description: __("Met mon site en autopilot avec de l'actualité sur le mot clé suivant : NASA. Je veux 2 posts par jour."),
    },
    {
        description: __("Sers-toi de ce flux RSS pour générer du contenu automatiquement tous les jours : https://www.nasa.gov/feed/"),
    },
    {
        description: __("Fais-moi une proposition d’articles basées sur les Google Trends du jour."),
    },
];

const form = reactive({
    language: getCurrentInstance().appContext.config.globalProperties.$app.default_locale,
    models: {
        text: "gpt-4o-mini",
        image: "flux_1_dev",
        avatar: "flux_1_dev",
    },
    settings: {
        categories: [],
        profiles: [],
        projects: [],
    },
});

const step = ref("step-1");
const currentProgressStep = ref("categories");
const debug = ref({});

provide("form", form);

const sendMessage = async () => {
    if (chat.processing) {
        return;
    }

    started.value = true;

    displayableMessages.value.push({
        role: "user",
        content: message.value,
    });

    if (form.settings) {
        chat.messages.push({
            role: "user",
            content: "JSON values: \n" + JSON.stringify(form.settings),
        });
    }

    chat.messages.push({
        role: "user",
        content: message.value,
    });

    chat.language = form.language;

    message.value = "";
    const response = await chat.post("one_click_chat");
    chat.messages.push({
        role: "assistant",
        content: response.full_response,
    });
    displayableMessages.value.push({
        role: "assistant",
        content: response.message,
    });

    if (response.settings) {
        form.settings = response.settings;
    }

    isTerminated.value = response.is_terminated;

    // debug
    if (response.settings) {
        debug.value = response.settings;
    }
};

const sendSuggestion = (suggestion, send) => {
    message.value = suggestion.trim();
    if (send) {
        sendMessage();
    }
};

const progressLogs = ref([]);

const submit = async () => {
    step.value = "step-2";

    // create categories
    let categories = [];
    if (form.settings && form.settings.categories && form.settings.categories.length > 0) {
        currentProgressStep.value = "categories";
        progressLogs.value.push({
            type: "pending",
            message: __("Création des catégories..."),
        });
        try {
            categories = await useForm({
                categories: reorderCategories(form.settings.categories),
            }).post("one_click_create_categories");

            progressLogs.value[progressLogs.value.length - 1] = {
                type: "success",
                message: __("Les catégories ont été créées avec succès."),
            };
        } catch (error) {
            progressLogs.value[progressLogs.value.length - 1] = {
                type: "error",
                message: __("Une erreur est survenue lors de la création des catégories :") + " " + (error.response.data?.message ?? error.message),
            };
        }
    } else {
        progressLogs.value.push({
            type: "success",
            message: __("Aucune catégorie à créer."),
        });
    }

    // create profiles
    currentProgressStep.value = "profiles";
    let profiles = [];
    if (form.settings && form.settings.profiles && form.settings.profiles.length > 0) {
        for (let i = 0; i < form.settings.profiles.length; i++) {
            progressLogs.value.push({
                type: "pending",
                message: __("Création du profil {firstname} {lastname}...", {
                    firstname: form.settings.profiles[i].first_name,
                    lastname: form.settings.profiles[i].last_name,
                }),
            });
            try {
                const profile = await useForm({
                    language: form.language,
                    model: form.models.avatar,
                    profile: form.settings.profiles[i],
                }).post("one_click_create_profile");
                progressLogs.value[progressLogs.value.length - 1] = {
                    type: "success",
                    message: __("Le profil {firstname} {lastname} a été créé avec succès.", {
                        firstname: profile.first_name,
                        lastname: profile.last_name,
                    }),
                };
                profiles.push(profile);
            } catch (error) {
                progressLogs.value[progressLogs.value.length - 1] = {
                    type: "error",
                    message:
                        __("Une erreur est survenue lors de la création du profil {firstname} {lastname} :", {
                            firstname: form.settings.profiles[i].first_name,
                            lastname: form.settings.profiles[i].last_name,
                        }) +
                        " " +
                        (error.response.data?.message ?? error.message),
                };
            }
        }
    } else {
        progressLogs.value.push({
            type: "success",
            message: __("Aucun profil à créer."),
        });
    }

    // create projects
    currentProgressStep.value = "projects";
    let projects = [];
    if (form.settings && form.settings.projects && form.settings.projects.length > 0) {
        for (let i = 0; i < form.settings.projects.length; i++) {
            progressLogs.value.push({
                type: "pending",
                message: __("Création du projet {name}...", {
                    name: form.settings.projects[i].name,
                }),
            });

            try {
                const project = await useForm({
                    language: form.language,
                    models: form.models,
                    categories,
                    profiles,
                    project: form.settings.projects[i],
                    template: form.settings.template,
                }).post("one_click_create_project");

                progressLogs.value[progressLogs.value.length - 1] = {
                    type: "success",
                    message: __("Le projet {name} a été créé avec succès.", {
                        name: project.name,
                    }),
                };
                projects.push(project);
            } catch (error) {
                progressLogs.value[progressLogs.value.length - 1] = {
                    type: "error",
                    message:
                        __("Une erreur est survenue lors de la création du projet {name} :", {
                            name: form.settings.projects[i].name,
                        }) +
                        " " +
                        (error.response.data?.message ?? error.message),
                };
            }
        }
    } else {
        progressLogs.value.push({
            type: "success",
            message: __("Aucun projet à créer."),
        });
    }

    progressLogs.value.push({
        type: "success",
        message: __("Toutes les étapes ont été effectuées avec succès."),
    });

    progressLogs.value.push({
        type: "pending",
        message: __("Redirection vers les projets dans 3..."),
    });

    setTimeout(() => {
        progressLogs.value[progressLogs.value.length - 1] = {
            type: "pending",
            message: __("Redirection vers les projets dans 3... 2..."),
        };
    }, 1000);

    setTimeout(() => {
        progressLogs.value[progressLogs.value.length - 1] = {
            type: "pending",
            message: __("Redirection vers les projets dans 3... 2... 1..."),
        };
    }, 2000);

    setTimeout(() => {
        router.push({ name: "projects.index" });
    }, 3000);
};

const canGenerate = computed(() => {
    return (
        isTerminated.value &&
        form.settings &&
        ((form.settings.categories && form.settings.categories.length > 0) ||
            (form.settings.profiles && form.settings.profiles.length > 0) ||
            (form.settings.projects && form.settings.projects.length > 0))
    );
});
</script>

<template>
    <div class="h-[calc(100vh-4rem)] overflow-hidden flex flex-col xl:h-screen">
        <template v-if="step === 'step-1'">
            <div class="overflow-y-hidden grid flex-1 grid-cols-2">
                <div
                    :class="[
                        'overflow-y-hidden flex flex-col py-6 space-y-3 lg:col-span-1 col-span-full duration-300 ease-in-out',
                        !started && false ? 'lg:translate-x-1/2' : '',
                    ]">
                    <Messages
                        v-if="started"
                        class="overflow-y-auto flex-1 px-2 lg:px-4 sm:px-3"
                        :messages="displayableMessages"
                        @suggestion-clicked="sendSuggestion($event, true)" />

                    <div v-if="chat.processing" class="flex-none px-2 lg:px-4 sm:px-3">
                        <Processing />
                    </div>

                    <div v-if="!started" class="overflow-auto flex flex-1 justify-center items-center px-2 lg:px-4 sm:px-3">
                        <Suggestions :suggestions="suggestions" @suggestion-clicked="sendSuggestion($event, false)" />
                    </div>

                    <div class="flex-none px-2 lg:px-4 sm:px-3">
                        <MessageBox v-model:message="message" @submit="sendMessage" :errors="chat.errors.get('messages')" />
                    </div>

                    <div v-if="canGenerate" class="flex flex-none justify-end px-2 lg:px-4 sm:px-3">
                        <PrimaryButton :processing="form.processing" @click="submit">{{ __("Générer") }}</PrimaryButton>
                    </div>
                </div>
                <div
                    :class="[
                        'lg:flex hidden overflow-y-hidden flex-col space-y-3 duration-300 ease-in-out',
                        !started && false ? 'opacity-0 pointer-events-none' : '',
                    ]">
                    <div class="overflow-y-auto flex-1 px-2 py-6 space-y-3 lg:px-4 sm:px-3">
                        <LanguageMenu v-model="form.language" />
                        <Models v-model:models="form.models" />
                        <Preview
                            v-if="form.settings"
                            v-model:settings="form.settings"
                            v-model:models="form.models"
                            :profiles="profiles"
                            :categories="categories" />
                    </div>
                </div>
            </div>
        </template>
        <div v-if="step === 'step-2'" class="mx-auto max-w-3xl py-8 space-y-6">
            <Progress :current-step="currentProgressStep" />
            <ProgressLogs :logs="progressLogs" />
        </div>
    </div>
</template>
